/**
 * Admins resource: TanStack-backed read + mutation hooks. Canonical client
 * interface for silo admin role management.
 *
 * `useAllAdmins` hides the TanStack machinery — consumers don't import
 * `@tanstack/react-query` themselves. Mutations (`useUpdateAdmin` /
 * `useDeleteAdmin`) auto-invalidate the silo's admins list on success so any
 * mounted list refetches. There's no paginated `useAdmins` hook yet — admin
 * counts are small and `admins.list` returns a single page — so this resource
 * exposes only the flat read; add one alongside if a paginated admin UI ever
 * needs it.
 *
 * The starred admin-dashboard tiles ride here too, as the caller's own
 * `/admins/{email}/starred` sub-resource: `useMyStarredTileKeys` reads them
 * and `useSetMyStarredTileKeys` replaces the whole ordered array. Both are
 * self-scoped, so they fill in the caller's email themselves rather than taking
 * one.
 *
 * There's one role write, `useUpdateAdmin` (`PUT /admins/{email}`): an
 * idempotent create-or-replace. Granting a brand-new admin and editing an
 * existing one are the same call, so callers adding several emails at once
 * (e.g. a pasted list) map over them and call `update` for each — adding an
 * already-existing admin is a well-defined upsert, not an error.
 *
 * See `./README.md` for the convention this file follows.
 */
import type { Admin, AdminTableRow as AdminTableRowWire } from '@repo/shared/orpc/procedures/admins';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore, useMyEmail, useSiloKey } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { usePaginatedQuery } from './use-paginated-query';
import { useSiloMutation } from './use-silo-mutation';
import { useSiloQuery } from './use-silo-query';

export type { Admin } from '@repo/shared/orpc/procedures/admins';

/**
 * A staff-table row as the dashboard renders it: the wire row plus the
 * client-side `key` that `TableGrid` uses to key rows (the `email`, which is
 * unique per admin). `key` is a rendering concern, so it's added here rather
 * than carried on the wire.
 */
export type AdminTableRow = AdminTableRowWire & { key: string };

/**
 * Page size `useAllAdmins` requests to pull every admin in one page. Matches
 * the contract's `cursorParams.limit` cap, so a single request returns the
 * whole set. Admins per silo number in the dozens, so one page always
 * suffices; this is a stop-gap until real pagination lands.
 */
const ALL_ADMINS_LIMIT = 100000;

/**
 * Every admin in the silo, flattened. Requests a single large page
 * (`ALL_ADMINS_LIMIT`) and returns the items as a flat array (`[]` until the
 * first page arrives). The role editor and channel-member picker read through
 * this.
 */
export function useAllAdmins(): { admins: Admin[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const result = usePaginatedQuery({ procedure: utils.admins.list, input: { limit: ALL_ADMINS_LIMIT } });
    const admins = useMemo(() => result.data?.pages.flatMap((page) => page.items) ?? [], [result.data]);
    return { admins, isLoading: result.isLoading };
}

/**
 * The editorial dashboard's staff table: every admin joined with their display
 * profile and post count, via `admins.getTable`. The server resolves the admin
 * name/photo overrides, so `row.profile` renders as-is. `rows` is `undefined`
 * until the first response; each row carries a client-side `key` (its `email`)
 * for `TableGrid`. `isLoading` / `isError` are surfaced so the caller can
 * render a spinner vs. an error state (rather than the hook firing a side
 * effect). Admin writes refetch the table through `useInvalidateAdmins` /
 * `deleteAdminAsync`, so there's no manual refresh. Search and pagination stay
 * client-side (the caller slices `rows`); admins per silo number in the dozens,
 * so one unpaginated read suffices until real pagination lands.
 */
export function useAdminTableRows(): { rows: AdminTableRow[] | undefined; isLoading: boolean; isError: boolean } {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.admins.getTable });
    const rows = useMemo(() => query.data?.map((row) => ({ ...row, key: row.email })), [query.data]);
    return { rows, isLoading: query.isLoading, isError: query.isError };
}

/**
 * The dashboard tiles the caller starred, in display order (`[]` until the read
 * lands). Backed by the self-scoped `admins.getStarred`, so the query stays
 * disabled until the caller's email is known. Every tile calls this — they
 * share one cached query rather than one request each.
 */
export function useMyStarredTileKeys(): { tileKeys: string[]; isLoading: boolean } {
    const email = useMyEmail();
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.admins.getStarred, input: { email: email ?? '' }, enabled: !!email });
    return { tileKeys: query.data?.tileKeys ?? [], isLoading: query.isLoading };
}

/**
 * Replace the caller's starred dashboard tiles with `tileKeys`, then invalidate
 * the read so mounted tiles re-render. One write covers starring, unstarring,
 * and reordering, so callers pass the complete new array rather than a delta —
 * compute it from `useMyStarredTileKeys`, which is the same cached value the
 * server last returned.
 */
export function useSetMyStarredTileKeys() {
    const siloKey = useSiloKey();
    const email = useMyEmail();
    const queryClient = useQueryClient();
    const utils = useApiQueryUtils();
    const mutation = useSiloMutation({
        procedure: utils.admins.setStarred,
        input: (tileKeys: string[]) => {
            // Only reachable from the admin dashboard, which no logged-out
            // caller renders; a null email here means the session went away
            // mid-interaction, and writing to `''` would just 400.
            if (!email) throw new Error('cannot set starred tiles without a signed-in caller');
            return { email, tileKeys };
        },
        onSuccess: () =>
            fireAndForget(
                queryClient.invalidateQueries({ queryKey: utils.admins.getStarred.key({ input: { siloKey, email: email ?? '' } }) }),
            ),
    });
    return { ...mutation, setStarredTileKeysAsync: mutation.mutateAsync };
}

/**
 * Invalidate the silo's admins reads so mounted readers refetch: both the lean
 * `admins.list` (role editor, member pickers) and the `admins.getTable` staff
 * table. A role write changes what either read returns, so both are dropped —
 * consumers don't have to refresh the table themselves.
 */
function useInvalidateAdmins() {
    const siloKey = useSiloKey();
    const queryClient = useQueryClient();
    const utils = useApiQueryUtils();
    return () =>
        fireAndForget(
            Promise.all([
                queryClient.invalidateQueries({ queryKey: utils.admins.list.key({ input: { siloKey } }) }),
                queryClient.invalidateQueries({ queryKey: utils.admins.getTable.key({ input: { siloKey } }) }),
            ]),
        );
}

/**
 * Grant or replace an admin's roles — one idempotent create-or-replace write
 * (`PUT /admins/{email}`). Use it both to add a new admin and to change an
 * existing one; adding an already-existing admin is an upsert, not an error.
 * Auto-invalidates the silo's admins list. Exposes the mutation's `mutateAsync`
 * as `updateAdminAsync` so call sites don't have to rename it themselves.
 */
export function useUpdateAdmin() {
    const utils = useApiQueryUtils();
    const invalidate = useInvalidateAdmins();
    const mutation = useSiloMutation({ procedure: utils.admins.update, onSuccess: invalidate });
    return { ...mutation, updateAdminAsync: mutation.mutateAsync };
}

/**
 * Revoke admin status. Auto-invalidates the silo's admins list.
 * Exposes the mutation's `mutateAsync` as `deleteAdminAsync` so call sites
 * don't have to rename it themselves.
 */
export function useDeleteAdmin() {
    const utils = useApiQueryUtils();
    const invalidate = useInvalidateAdmins();
    const mutation = useSiloMutation({ procedure: utils.admins.delete, onSuccess: invalidate });
    return { ...mutation, deleteAdminAsync: mutation.mutateAsync };
}

/**
 * Revoke admin status and invalidate the affected caches. Plain function —
 * callable from anywhere a `Datastore` is available, not just inside React
 * (e.g. the editorial-dash actions-menu action). Invalidates both admins reads
 * (lean list + staff table), mirroring `useInvalidateAdmins`, so the staff
 * table refetches without a separate refresh. `useDeleteAdmin` is the
 * `useMutation`-flavoured wrapper for callers that want `isPending` /
 * `onSuccess` composition.
 */
export async function deleteAdminAsync(datastore: Datastore, input: { siloKey: string; email: string }): Promise<{ email: string }> {
    const result = await datastore.getApiClient().admins.delete({ ...input, locale: datastore.getLanguage() });
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(
        Promise.all([
            queryClient.invalidateQueries({ queryKey: utils.admins.list.key({ input: { siloKey: input.siloKey } }) }),
            queryClient.invalidateQueries({ queryKey: utils.admins.getTable.key({ input: { siloKey: input.siloKey } }) }),
        ]),
    );
    return result;
}

/**
 * The caller's own current moderation-dashboard channel, or `null` if unset.
 * Backed by the self-scoped `admins.getCurrentChannel`, so it works for any
 * admin — including a limited editorial user without `channel/view`.
 */
export function useCurrentChannelKey(): { channelKey: string | null; isLoading: boolean } {
    const email = useMyEmail();
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.admins.getCurrentChannel, input: { email: email ?? '' }, enabled: !!email });
    return { channelKey: query.data?.channelKey ?? null, isLoading: query.isLoading };
}

/**
 * Set the caller's current moderation-dashboard channel, auto-invalidating the
 * `useCurrentChannelKey` read so it reflects the new selection. Exposes
 * `setCurrentChannelAsync` for call sites that fire-and-forget the write.
 */
export function useSetCurrentChannel() {
    const siloKey = useSiloKey();
    const email = useMyEmail();
    const queryClient = useQueryClient();
    const utils = useApiQueryUtils();
    const mutation = useSiloMutation({
        procedure: utils.admins.setCurrentChannel,
        // The caller's own email is ambient too; call sites pass channelKey.
        input: (vars: { channelKey: string }) => ({ ...vars, email: email ?? '' }),
        onSuccess: () =>
            fireAndForget(
                queryClient.invalidateQueries({
                    queryKey: utils.admins.getCurrentChannel.key({ input: { siloKey, email: email ?? '' } }),
                }),
            ),
    });
    return { ...mutation, setCurrentChannelAsync: mutation.mutateAsync };
}
